import type { DeliveryJob, PickupJob, Task } from '../task/task.interface';
import { TASK_JOB_STATUS, TASK_JOB_TYPE } from '../task/task.interface';
import type { EnrichedStop, EnrichedStopJob } from '../stops-queue/stops-queue.interface';
import { SITE_TYPE } from '../site/site.interface';
import { ObjectFactory } from './object.factory';
import { orderFactory } from './order.factory';
import { taskFactory } from './task.factory';

const DEFAULT_DELIVERY_JOB: DeliveryJob = {
  data: {},
  id: 'job-delivery-001',
  sortIndex: 0,
  status: TASK_JOB_STATUS.TO_DO,
  type: TASK_JOB_TYPE.DELIVERY,
};

const DEFAULT_PICKUP_JOB: PickupJob = {
  data: {
    durationTime: 10,
    site: { code: 'WH1', color: '#000', id: 'site-1', name: 'Warehouse', type: SITE_TYPE.WAREHOUSE },
  },
  id: 'job-pickup-001',
  sortIndex: 0,
  status: TASK_JOB_STATUS.TO_DO,
  type: TASK_JOB_TYPE.PICKUP,
};

/**
 * Builds an internally-consistent `EnrichedStopJob`: the `order` is built first, the `task` embeds
 * that same order's summary as `task.order` (so `order.id === task.order.id`), and `taskJob` is
 * derived from `task.jobs` (so `taskJob` is always a member of `task.jobs`) rather than a
 * standalone constant.
 */
function buildConsistentStopJob(taskOverrides: Partial<Task> = {}): EnrichedStopJob {
  const order = orderFactory.build();
  const task = taskFactory.build({
    ...taskOverrides,
    order: { id: order.id, tags: order.tags, totalHandlingUnitQuantity: order.totalHandlingUnitQuantity },
  });

  return {
    order,
    task,
    taskJob: task.jobs[0],
  };
}

export class EnrichedStopFactory extends ObjectFactory<EnrichedStop> {
  protected getDefaults(): EnrichedStop {
    return {
      fullAddress: '10 Rue de la Paix, 75001 Paris, France',
      id: 'stop-001',
      jobs: [buildConsistentStopJob()],
      timeSlot: undefined,
    };
  }

  /** Builds an enriched stop wrapping a delivery job; `dataOverrides` fully replaces the (empty) job data defaults. */
  buildDeliveryStop(dataOverrides: Partial<DeliveryJob['data']> = {}): EnrichedStop {
    const taskJob: DeliveryJob = { ...DEFAULT_DELIVERY_JOB, data: { ...dataOverrides } };
    const stopJob = buildConsistentStopJob({ jobs: [taskJob] });
    return this.build({ jobs: [stopJob] });
  }

  /** Builds an enriched stop wrapping a pickup job; `dataOverrides` merges onto the pickup job data defaults. */
  buildPickupStop(dataOverrides: Partial<PickupJob['data']> = {}): EnrichedStop {
    const taskJob: PickupJob = { ...DEFAULT_PICKUP_JOB, data: { ...DEFAULT_PICKUP_JOB.data, ...dataOverrides } };
    const stopJob = buildConsistentStopJob({ jobs: [taskJob] });
    return this.build({ jobs: [stopJob] });
  }
}

export const enrichedStopFactory = new EnrichedStopFactory();
